import React, { useEffect, useMemo, useState } from "react";
import { api, CATEGORY_LABELS, STATUS_LABELS, PRIORITY_LABELS, userGreetingName, userGender } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ComposeDialog from "@/components/ComposeDialog";
import SynapseLoader from "@/components/SynapseLoader";
import InstallAppButton from "@/components/InstallAppButton";
import { PregnancyAlertsCard } from "@/components/PregnancyAlertsCard";
import { IntakePendingCard } from "@/components/IntakePendingCard";
import { IntakeReceivedCard } from "@/components/IntakeReceivedCard";
import {
  MessagesSquare, MailOpen, AlertOctagon, Send, Plus, ShieldCheck, ScrollText,
  ChevronRight, Search, ArrowUp, ArrowDown, Archive, FileCheck2, FolderOpen,
  Salad, Download, Mail, MailCheck, FileClock, ClipboardList, Stethoscope,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";

// Pallino semplificato a 3 colori:
//  rosso  = richiesta critica/alta non completata (urgente da gestire)
//  arancio = richiesta normale non completata
//  verde  = completata
//  grigio = rifiutata/annullata (archiviata senza risposta)
function dotFor(t) {
  const st = t.request_status || "inviata";
  if (st === "completata") return { cls: "bg-emerald-500", label: "Completata" };
  if (st === "rifiutata" || st === "annullata") return { cls: "bg-zinc-400", label: st === "rifiutata" ? "Rifiutata" : "Annullata" };
  const urgent = t.priority === "critica" || t.priority === "alta";
  if (urgent) return { cls: "bg-red-500 ring-2 ring-red-100", label: "Urgente da gestire" };
  return { cls: "bg-orange-500", label: "Da gestire" };
}

// true = l'utente ha creato il thread (inviata da lui); false = ricevuta
function isOutgoing(t, userId) {
  return t.created_by === userId;
}

const ARCHIVED_STATUSES = ["completata", "rifiutata", "annullata"];

const FILTERS = [
  { k: "tutte",      l: "Tutte",      desc: "Tutte le comunicazioni" },
  { k: "inviate",    l: "Inviate",    desc: "Richieste che hai inviato" },
  { k: "ricevute",   l: "Ricevute",   desc: "Richieste che hai ricevuto" },
  { k: "archiviate", l: "Archiviate", desc: "Completate, rifiutate o annullate" },
];

function matchesFilter(t, f, userId) {
  const st = t.request_status || "inviata";
  const isArchived = ARCHIVED_STATUSES.includes(st);
  if (f === "tutte")      return !isArchived;
  if (f === "inviate")    return isOutgoing(t, userId) && !isArchived;
  if (f === "ricevute")   return !isOutgoing(t, userId) && !isArchived;
  if (f === "archiviate") return isArchived;
  return true;
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  if (same) return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isSpecialist = user?.profession === "medico_specialista" || user?.profession === "medico_medicina_generale" || user?.profession === "biologo_nutrizionista";
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [threads, setThreads] = useState(null);
  const [recentPlans, setRecentPlans] = useState(null);  // Ultimi piani nutrizionali
  const [composeOpen, setComposeOpen] = useState(false);
  const [filter, setFilter] = useState("tutte");
  const [q, setQ] = useState("");

  const isNutritionist = user?.profession === "biologo_nutrizionista";
  const isMMG = user?.profession === "medico_medicina_generale";
  const isCaseDoctor = isMMG || user?.profession === "medico_specialista";
  const [recentCases, setRecentCases] = useState(null);
  const [casesCounters, setCasesCounters] = useState({});

  const load = async () => {
    const [s, t, plans, cases] = await Promise.all([
      api.get("/stats").catch(() => ({ data: null })),
      api.get("/threads").catch(() => ({ data: [] })),
      isNutritionist
        ? api.get("/referto/recent-nutrition-plans?limit=6").catch(() => ({ data: { items: [] } }))
        : Promise.resolve({ data: { items: [] } }),
      isMMG
        ? api.get("/cases?limit=6").catch(() => ({ data: { items: [], counters: {} } }))
        : Promise.resolve({ data: { items: [], counters: {} } }),
    ]);
    setStats(s.data);
    setThreads(t.data || []);
    setRecentPlans(plans.data?.items || []);
    setRecentCases(cases.data?.items || []);
    setCasesCounters(cases.data?.counters || {});
  };
  useEffect(() => { load(); }, [isNutritionist, isCaseDoctor]);

  const kpis = [
    { label: "Da gestire", value: stats?.da_gestire ?? 0, icon: MailOpen,       color: "text-amber-700",  bg: "bg-amber-50 border border-amber-200/60",  testid: "kpi-da-gestire", filter: "da_gestire" },
    { label: "Urgenti",    value: stats?.urgenti ?? 0,    icon: AlertOctagon,   color: "text-red-700",     bg: "bg-red-50 border border-red-200/60",     testid: "kpi-urgenti",    filter: "urgenti" },
    { label: "Completate", value: stats?.completate ?? 0, icon: MessagesSquare, color: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/60", testid: "kpi-completate", filter: "completata" },
    { label: "Inviate",    value: stats?.sent ?? 0,       icon: Send,           color: "text-slate-700",   bg: "bg-slate-100 border border-slate-200",  testid: "kpi-sent",       filter: "inviate" },
  ];

  const filtered = useMemo(() => {
    if (!threads) return [];
    const query = q.trim().toLowerCase();
    const rank = (t) => {
      const st = t.request_status || "inviata";
      const isClosed = ["completata", "rifiutata", "annullata"].includes(st);
      if (t.priority === "critica" && !isClosed) return 0;
      if (t.priority === "alta" && !isClosed) return 1;
      if (["inviata", "ricevuta"].includes(st)) return 2;
      if (["presa_in_carico", "in_lavorazione"].includes(st)) return 3;
      if (st === "completata") return 4;
      return 5;
    };
    return threads
      .filter((t) => matchesFilter(t, filter, user?.id))
      .filter((t) => !query || `${t.subject} ${t.patient_name || ""}`.toLowerCase().includes(query))
      .sort((a, b) => {
        const r = rank(a) - rank(b);
        if (r !== 0) return r;
        return new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0);
      });
  }, [threads, filter, q, user?.id]);

  return (
    <div className="p-3 md:p-8 max-w-7xl mx-auto bg-[#EEF1F5] min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4 md:mb-6">
        <div>
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-[#1E88E5] hidden md:block">
            Panoramica
          </p>
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mt-0 md:mt-1 leading-tight">
            {userGender(user) === "F" ? "Benvenuta" : "Benvenuto"}
            {userGreetingName(user) && (
              <span className="block text-slate-900"
                    data-testid="dashboard-greeting-name">
                {userGreetingName(user)}
              </span>
            )}
          </h1>
          <p className="text-slate-500 mt-1 text-sm hidden md:block">
            Tutte le tue comunicazioni cliniche, sotto controllo.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <InstallAppButton variant="outline" />
          {user?.is_admin && (
            <>
              <Button onClick={() => navigate("/admin/verifiche")} data-testid="dashboard-admin-verif"
                      variant="outline"
                      className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50 h-11">
                <ShieldCheck className="h-4 w-4 mr-2 text-emerald-600" /> Verifica professionisti
              </Button>
              <Button onClick={() => navigate("/admin/audit")} data-testid="dashboard-admin-audit"
                      variant="outline"
                      className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50 h-11">
                <ScrollText className="h-4 w-4 mr-2 text-sky-600" /> Audit log
              </Button>
            </>
          )}
          <Button onClick={() => setComposeOpen(true)} data-testid="new-message-btn"
                  className="h-11 rounded-lg bg-[#1E88E5] hover:bg-[#1976D2] border border-[#1E88E5]
                             text-white font-semibold shadow-sm transition-colors">
            <Plus className="h-4 w-4 mr-2" /> Nuova comunicazione
          </Button>
          {isSpecialist && (
            <Button onClick={() => navigate(isMMG ? "/nuova-valutazione" : "/nuovo-referto")}
                    data-testid="new-referto-btn"
                    className="h-11 rounded-lg bg-[#1E88E5] hover:bg-[#1976D2]
                               text-white font-semibold shadow-sm transition-colors">
              <FileCheck2 className="h-4 w-4 mr-2" />
              {isMMG
                ? "Nuova valutazione clinica"
                : (user?.profession === "biologo_nutrizionista"
                    ? "Nuova valutazione nutrizionale"
                    : "Nuova valutazione clinica")}
            </Button>
          )}
          {isMMG && (
            <Button variant="outline" onClick={() => navigate("/casi")}
                    data-testid="casi-clinici-btn"
                    className="h-11 rounded-lg border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
              <ClipboardList className="h-4 w-4 mr-2 text-slate-500" /> Casi clinici
            </Button>
          )}
          {isSpecialist && (
            <Button variant="outline" onClick={() => navigate("/cartelle-pazienti")}
                    data-testid="cartelle-pazienti-btn"
                    className="h-11 rounded-lg border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
              <FolderOpen className="h-4 w-4 mr-2 text-[#1E88E5]" /> Cartelle pazienti
            </Button>
          )}
        </div>
      </div>

      {/* KPI cards — puliti, professionali */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
        {kpis.map((k) => (
          <Card key={k.label} data-testid={k.testid}
                onClick={() => navigate(`/messaggi?filter=${k.filter}`)}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/messaggi?filter=${k.filter}`); }}
                className="group p-3 md:p-5 border-slate-200 shadow-sm rounded-xl bg-white cursor-pointer transition-all hover:shadow-md hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40">
            <div className="flex items-center justify-between">
              <div className={`h-8 w-8 md:h-10 md:w-10 rounded-lg ${k.bg} ${k.color} flex items-center justify-center`}>
                <k.icon className="h-4 w-4 md:h-5 md:w-5" />
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-900 transition-colors" />
            </div>
            <div className="mt-2 md:mt-3 font-heading text-2xl md:text-3xl font-extrabold text-slate-900 tabular-nums">{k.value}</div>
            <div className="text-xs md:text-sm text-slate-500">{k.label}</div>
          </Card>
        ))}
      </div>

      {/* Ultimi piani nutrizionali — solo per biologo nutrizionista */}
      {isNutritionist && (
        <RecentPlansCard plans={recentPlans} onCreate={() => navigate("/nuovo-referto")}
                         onOpenFolder={() => navigate("/cartelle-pazienti")} />
      )}

      {/* Casi clinici — solo MMG (gli specialisti usano la valutazione clinica/referto) */}
      {isSpecialist && <IntakeReceivedCard />}
      {isSpecialist && <IntakePendingCard />}
      {isCaseDoctor && <PregnancyAlertsCard />}
      {isMMG && (
        <RecentCasesCard cases={recentCases} counters={casesCounters}
                         onCreate={() => navigate("/nuova-valutazione")}
                         onOpenAll={() => navigate("/casi")} />
      )}

      {/* Tutte le richieste */}
      <Card className="mt-4 md:mt-6 p-3 md:p-5 rounded-xl border-slate-200 shadow-sm bg-white" data-testid="dashboard-requests-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 md:gap-3 mb-3">
          <div>
            <h3 className="font-heading text-base md:text-lg font-bold text-slate-900">Tutte le richieste</h3>
            <p className="text-xs text-slate-500 hidden md:block">Colpo d'occhio su ogni comunicazione clinica</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Cerca…"
                     className="pl-9 h-9 md:w-56 w-full" data-testid="dashboard-req-search" />
            </div>
          </div>
        </div>

        {/* Tabs con conteggio */}
        <div className="flex gap-1 flex-wrap text-[12px] font-semibold mb-3 overflow-x-auto -mx-1 px-1 md:mx-0 md:px-0">
          {FILTERS.map((f) => {
            const count = threads
              ? threads.filter((t) => matchesFilter(t, f.k, user?.id)).length
              : null;
            const active = filter === f.k;
            return (
              <button key={f.k} onClick={() => setFilter(f.k)}
                      data-testid={`dashboard-filter-${f.k}`}
                      title={f.desc}
                      className={`px-3 py-1.5 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                        active
                          ? "bg-[#1E88E5] text-white shadow-sm"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                      }`}>
                {f.k === "archiviate" && <Archive className={`h-3.5 w-3.5 ${active ? "text-white/90" : "text-slate-500"}`} />}
                {f.l}
                {count !== null && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-white/20 text-white" : "bg-white text-slate-500 border border-slate-200"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Legenda semplificata: 3 colori chiave */}
        <div className="hidden md:flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 mb-3 border-b border-slate-100 pb-2">
          <span className="uppercase font-bold tracking-widest text-slate-400">Legenda</span>
          <LegendDot cls="bg-red-500 ring-2 ring-red-100" label="Urgente da gestire" />
          <LegendDot cls="bg-amber-500" label="Da gestire" />
          <LegendDot cls="bg-emerald-500" label="Completata" />
          <LegendDot cls="bg-zinc-400" label="Rifiutata/Annullata" />
          <span className="ml-2 pl-2 border-l border-slate-200 flex items-center gap-1">
            <ArrowUp className="h-3 w-3 text-[#1E88E5]" /> Inviata da te
          </span>
          <span className="flex items-center gap-1">
            <ArrowDown className="h-3 w-3 text-emerald-600" /> Ricevuta
          </span>
        </div>

        <div className="divide-y divide-slate-100" data-testid="dashboard-requests-list">
          {threads === null && (
            <div className="py-8 flex justify-center"><SynapseLoader label="Sto caricando le richieste…" /></div>
          )}
          {threads !== null && filtered.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-400">
              Nessuna richiesta in questa vista.
            </div>
          )}
          {filtered.map((t) => {
            const d = dotFor(t);
            const status = STATUS_LABELS[t.request_status || "inviata"] || STATUS_LABELS.inviata;
            const category = CATEGORY_LABELS[t.request_type || t.last_category] || "";
            const outgoing = isOutgoing(t, user?.id);
            const DirArrow = outgoing ? ArrowUp : ArrowDown;
            const dirColor = outgoing ? "text-[#1E88E5]" : "text-emerald-600";
            const dirLabel = outgoing ? "Inviata da te" : "Ricevuta";
            return (
              <button key={t.id} onClick={() => navigate(`/messaggi/${t.id}`)}
                      data-testid={`dashboard-req-${t.id}`}
                      className="w-full text-left px-1 py-3 hover:bg-slate-50 rounded-md flex items-start gap-3 transition-colors">
                <span className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${d.cls}`}
                      title={d.label}
                      data-testid={`dashboard-req-dot-${t.id}`} />
                <DirArrow className={`mt-1 h-3.5 w-3.5 flex-shrink-0 ${dirColor}`}
                          data-testid={`dashboard-req-direction-${t.id}`}
                          data-direction={outgoing ? "outgoing" : "incoming"}
                          title={dirLabel} strokeWidth={2.6} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">{t.subject || "(Senza oggetto)"}</div>
                    <span className="text-[11px] text-slate-400 flex-shrink-0">{formatWhen(t.last_message_at)}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {t.patient_name && <span>Paziente: <span className="text-slate-700 font-medium">{t.patient_name}</span> · </span>}
                    {category && <span>{category} · </span>}
                    <span>{status.label}</span>
                    {t.priority && t.priority !== "normale" && (
                      <> · <span className="text-slate-700 font-medium">Priorità {PRIORITY_LABELS[t.priority] || t.priority}</span></>
                    )}
                  </div>
                </div>
                {t.unread_count > 0 && (
                  <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full
                                   bg-[#1E88E5]
                                   text-white text-[10px] font-bold shadow-sm"
                        data-testid={`dashboard-req-unread-${t.id}`}>
                    {t.unread_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {threads !== null && filtered.length > 0 && (
          <div className="mt-3 flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => navigate("/messaggi")} data-testid="goto-messages"
                    className="text-slate-700 hover:bg-slate-100 hover:text-slate-900">
              Vai alle conversazioni <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </Card>

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen} onCreated={load} />

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3 shadow-sm">
        <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
          <span className="font-heading font-bold text-sm">i</span>
        </div>
        <div className="text-xs text-slate-500 leading-relaxed">
          <span className="font-semibold text-slate-700">Privacy-by-design.</span>{" "}
          Le notifiche email non contengono mai nome paziente o contenuto messaggio.
          Ogni accesso alle conversazioni cliniche è tracciato (audit log GDPR art. 30).
          Inserisci solo dati strettamente necessari alla comunicazione.
        </div>
      </div>
    </div>
  );
}

function LegendDot({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`h-2 w-2 rounded-full ${cls}`} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Ultimi piani nutrizionali — riquadro dashboard per biologo nutrizionista
// ---------------------------------------------------------------------------
const PLAN_STATUS = {
  draft:    { cls: "bg-amber-500",   ring: "ring-amber-100",   text: "text-amber-700",   Icon: FileClock,
              label: "In bozza · da completare" },
  complete: { cls: "bg-sky-500",     ring: "ring-sky-100",     text: "text-sky-700",     Icon: Mail,
              label: "Completo · non ancora inviato" },
  sent:     { cls: "bg-emerald-500", ring: "ring-emerald-100", text: "text-emerald-700", Icon: MailCheck,
              label: "Inviato al paziente" },
};

function fmtPlanDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function RecentPlansCard({ plans, onCreate, onOpenFolder }) {
  const counts = React.useMemo(() => {
    const c = { draft: 0, complete: 0, sent: 0 };
    (plans || []).forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [plans]);
  return (
    <Card className="mt-4 md:mt-6 p-3 md:p-5 rounded-xl border-emerald-200 shadow-sm
                     bg-white"
          data-testid="dashboard-recent-plans">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Salad className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-base md:text-lg font-bold text-slate-900">
              Ultimi piani nutrizionali
            </h3>
            <p className="text-xs text-slate-500 hidden md:block">
              Stato di completamento e invio al paziente delle ultime valutazioni.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill status="draft" count={counts.draft} />
          <StatusPill status="complete" count={counts.complete} />
          <StatusPill status="sent" count={counts.sent} />
          <Button variant="outline" size="sm" onClick={onOpenFolder}
                  data-testid="recent-plans-open-folders"
                  className="h-8 border-emerald-200 text-emerald-800 bg-white hover:bg-emerald-50">
            <FolderOpen className="h-3.5 w-3.5 mr-1.5" /> Cartelle
          </Button>
        </div>
      </div>

      {plans === null ? (
        <div className="py-8 flex justify-center">
          <SynapseLoader label="Carico i piani…" />
        </div>
      ) : plans.length === 0 ? (
        <div className="py-8 text-center">
          <Salad className="h-10 w-10 text-emerald-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500 mb-3">
            Ancora nessun piano nutrizionale. Crea la prima valutazione per iniziare.
          </div>
          <Button onClick={onCreate} size="sm" data-testid="recent-plans-create-first"
                  className="h-9 bg-[#1E88E5] hover:bg-[#1976D2] text-white">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Nuova valutazione
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="recent-plans-list">
          {plans.map((p, idx) => {
            const s = PLAN_STATUS[p.status] || PLAN_STATUS.complete;
            const StatusIcon = s.Icon;
            return (
              <div key={p.referto_id || idx}
                   data-testid={`plan-row-${idx}`}
                   data-status={p.status}
                   className="py-3 px-1 flex items-start gap-3 hover:bg-white/70 rounded-md transition-colors">
                {/* Slash / pallino colorato per stato */}
                <span className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${s.cls} ring-2 ${s.ring}`}
                      title={p.status_label}
                      data-testid={`plan-status-dot-${idx}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">
                      {p.patient_name || "(Paziente senza nome)"}
                    </div>
                    <span className="text-[11px] text-slate-400 flex-shrink-0">
                      {fmtPlanDate(p.confirmed_at || p.created_at)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 truncate">
                    {p.diet_type && <span className="text-purple-700 font-medium">{p.diet_type}</span>}
                    {p.diet_type && p.target_kcal && <span> · </span>}
                    {p.target_kcal && <span>{p.target_kcal} kcal</span>}
                    {(p.diet_type || p.target_kcal) && p.visit_reason && <span> · </span>}
                    {p.visit_reason && <span className="text-slate-600">{p.visit_reason}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <StatusIcon className={`h-3 w-3 ${s.text}`} />
                    <span className={`text-[11px] font-semibold ${s.text}`}>{s.label}</span>
                  </div>
                </div>
                {p.download_url && (
                  <a href={p.download_url} target="_blank" rel="noreferrer"
                     data-testid={`plan-download-${idx}`}
                     title="Scarica PDF"
                     className="mt-1 p-1.5 rounded-md text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 flex-shrink-0">
                    <Download className="h-4 w-4" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function StatusPill({ status, count }) {
  const s = PLAN_STATUS[status];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold
                      ${s.text} bg-white border border-slate-200`}
          data-testid={`plan-status-pill-${status}`}>
      <span className={`h-2 w-2 rounded-full ${s.cls}`} />
      {count} {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Casi clinici — riquadro dashboard per MMG (Fase 1)
// ---------------------------------------------------------------------------
const CASE_STATUS = {
  aperto:               { cls: "bg-amber-500",   text: "text-amber-700",   label: "Aperto" },
  in_gestione:          { cls: "bg-blue-500",    text: "text-blue-700",    label: "In gestione" },
  inviato_specialista:  { cls: "bg-indigo-500",  text: "text-indigo-700",  label: "Inviato" },
  in_attesa:            { cls: "bg-purple-500",  text: "text-purple-700",  label: "In attesa" },
  risposta_ricevuta:    { cls: "bg-teal-500",    text: "text-teal-700",    label: "Risposta" },
  chiuso:               { cls: "bg-emerald-500", text: "text-emerald-700", label: "Chiuso" },
};

function RecentCasesCard({ cases, counters, onCreate, onOpenAll }) {
  const totOpen = (counters.aperto || 0) + (counters.in_gestione || 0) +
                  (counters.inviato_specialista || 0) + (counters.in_attesa || 0);
  return (
    <Card className="mt-4 md:mt-6 p-3 md:p-5 rounded-xl border-blue-200 shadow-sm
                     bg-white"
          data-testid="dashboard-recent-cases">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-base md:text-lg font-bold text-slate-900">
              Casi clinici
            </h3>
            <p className="text-xs text-slate-500 hidden md:block">
              Ultime valutazioni cliniche e stato del percorso.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {totOpen > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200"
                  data-testid="cases-open-pill">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              {totOpen} aperti / in corso
            </span>
          )}
          {(counters.chiuso || 0) > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200"
                  data-testid="cases-closed-pill">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {counters.chiuso} chiusi
            </span>
          )}
          <Button variant="outline" size="sm" onClick={onOpenAll}
                  data-testid="cases-open-all"
                  className="h-8 border-blue-200 text-blue-800 bg-white hover:bg-blue-50">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Tutti
          </Button>
        </div>
      </div>

      {cases === null ? (
        <div className="py-8 text-center text-slate-500 text-sm">Carico i casi…</div>
      ) : cases.length === 0 ? (
        <div className="py-8 text-center">
          <ClipboardList className="h-10 w-10 text-blue-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500 mb-3">
            Nessun caso ancora. Crea la prima valutazione clinica.
          </div>
          <Button onClick={onCreate} size="sm" data-testid="cases-empty-create"
                  className="h-9 bg-[#1E88E5] hover:bg-[#1976D2] text-white">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Nuova valutazione
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="cases-list">
          {cases.map((c, idx) => {
            const s = CASE_STATUS[c.status] || CASE_STATUS.aperto;
            return (
              <div key={c.id || idx}
                   data-testid={`case-dash-row-${idx}`}
                   data-status={c.status}
                   className="py-3 px-1 flex items-start gap-3 hover:bg-white/70 rounded-md transition-colors cursor-pointer"
                   onClick={onOpenAll}>
                <span className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${s.cls} ring-2 ring-slate-100`}
                      title={s.label} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">
                      {c.patient_first_name} {c.patient_last_name}
                    </div>
                    <span className={`text-[11px] font-semibold ${s.text} flex-shrink-0`}>
                      {s.label}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 truncate">
                    {(c.reason_codes || []).join(" · ") || "—"}
                    {c.reason_free_text && ` · ${c.reason_free_text.slice(0, 80)}`}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 mt-1 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
